import { forwardRef, useState } from "react";
import "./SegmentedOTPInput.css";

/**
 * SegmentedOTPInput
 * Six visual digit boxes driven by a single real (transparent) input so
 * iOS / Android SMS autofill ("one-time-code") and paste keep working.
 * Drop-in replacement for OTPInput — identical props and ref contract.
 *
 * Props:
 *  - value      : string of digits
 *  - onChange   : (digits) => void
 *  - onComplete : (digits) => void   fires when length === `length`
 *  - length     : expected digit count (default 6)
 *  - disabled   : boolean
 *  - error      : string             shown as error message under boxes
 *  - autoFocus  : boolean
 *  - ariaLabel  : accessible label
 */
const SegmentedOTPInput = forwardRef(function SegmentedOTPInput(
  {
    value = "",
    onChange,
    onComplete,
    length = 6,
    disabled = false,
    error = "",
    autoFocus = false,
    ariaLabel,
  },
  ref
) {
  const [focused, setFocused] = useState(false);

  const handleChange = (e) => {
    const digits = e.target.value.replace(/\D/g, "").slice(0, length);
    onChange?.(digits);
    if (digits.length === length) {
      onComplete?.(digits);
    }
  };

  const activeIndex = Math.min(value.length, length - 1);

  return (
    <div
      className={`sotp-wrapper ${error ? "has-error" : ""} ${
        disabled ? "is-disabled" : ""
      }`}
    >
      <div className="sotp-row" dir="ltr">
        {Array.from({ length }, (_, i) => (
          <div
            key={i}
            className={`sotp-box ${value[i] ? "is-filled" : ""} ${
              focused && i === activeIndex && !disabled ? "is-active" : ""
            }`}
            aria-hidden="true"
          >
            <span className="sotp-digit">{value[i] || ""}</span>
          </div>
        ))}
        <input
          ref={ref}
          type="text"
          inputMode="numeric"
          pattern="[0-9]*"
          autoComplete="one-time-code"
          maxLength={length}
          value={value}
          onChange={handleChange}
          onFocus={() => setFocused(true)}
          onBlur={() => setFocused(false)}
          disabled={disabled}
          autoFocus={autoFocus}
          className="sotp-input"
          dir="ltr"
          aria-label={ariaLabel || `קוד אימות בן ${length} ספרות`}
          data-length={length}
        />
      </div>
      {error && <span className="sotp-error">{error}</span>}
    </div>
  );
});

export default SegmentedOTPInput;
